/* eslint-disable prefer-arrow-callback */
import { type MutableRefObject, useEffect, useRef } from "react";

import { SCENARIO_SCROLL } from "consts";

import type { ScenarioScroll } from "pages/app/scenario-analysis/hooks/useScenarioScroll";
import { getScrollParent, getScrollTarget, getScrollTop } from "pages/app/scenario-analysis/utils/scrollUtils";

type UseFieldImportLandingScrollArgs = {
  scroll: ScenarioScroll;
  // True while the page is on the project a field import landed on; cleared when the user leaves it, so pair it with landingRecalcPendingRef for one-shot behaviour.
  isActiveRef: MutableRefObject<boolean>;
  showTransitionModal: boolean;
  isHardLoading: boolean;
  hydrationApplied: boolean;
  isCalculating: boolean;
  // Set by the page's recalc handlers; true means land on the chart once the recalc settles.
  scrollToChartAfterRecalcRef: MutableRefObject<boolean>;
  // Set only for the landing's own auto-recalc and cleared once it settles, as isActiveRef stays true for later runs on that project.
  landingRecalcPendingRef: MutableRefObject<boolean>;
};

// Field-import landing scroll: keeps the builder anchored while the transition modal is open, then lands on the chart or builder.
export const useFieldImportLandingScroll = ({
  scroll,
  isActiveRef,
  showTransitionModal,
  isHardLoading,
  hydrationApplied,
  isCalculating,
  scrollToChartAfterRecalcRef,
  landingRecalcPendingRef,
}: UseFieldImportLandingScrollArgs) => {
  const { scrollToBuilder, scrollToChart, scheduleBuilderScrollForImportLanding, reanchorBuilderIfDrifted, builderRef } = scroll;
  // Tracks the previous calculating state so we can detect when a recalc just finished.
  const wasCalculatingRef = useRef(false);

  // Re-anchors on the builder when layout reflow drifts the real scroll container while the transition modal is open.
  useEffect(
    function reanchorBuilderOnScrollDriftDuringTransition() {
      if (!isActiveRef.current || !showTransitionModal) return undefined;

      const scrollContainer = getScrollParent(builderRef.current?.parentElement ?? null);
      const scrollTarget = getScrollTarget(scrollContainer);

      let lastScrollY = getScrollTop(scrollContainer);
      function reanchorOnLargeScroll() {
        const next = getScrollTop(scrollContainer);
        if (Math.abs(next - lastScrollY) >= SCENARIO_SCROLL.SCROLL_DRIFT_THRESHOLD_PX) reanchorBuilderIfDrifted();
        lastScrollY = next;
      }

      scrollTarget.addEventListener("scroll", reanchorOnLargeScroll, { passive: true });
      const rafId = requestAnimationFrame(reanchorBuilderIfDrifted);

      return function stopReanchoringScrollDrift() {
        cancelAnimationFrame(rafId);
        scrollTarget.removeEventListener("scroll", reanchorOnLargeScroll);
      };
    },
    [showTransitionModal, reanchorBuilderIfDrifted, isActiveRef, builderRef]
  );

  // Anchors on the builder while the transition modal is up; re-runs as hydration and auto-recalc reflow the page.
  useEffect(
    function anchorBuilderWhileTransitionModalOpen() {
      if (!isActiveRef.current) return undefined;
      if (!showTransitionModal || isHardLoading) return undefined;
      return scheduleBuilderScrollForImportLanding();
    },
    [showTransitionModal, isHardLoading, hydrationApplied, isCalculating, scheduleBuilderScrollForImportLanding, isActiveRef]
  );

  // After the landing's own recalc settles: chart if the user asked for it and the modal is closed, else the builder.
  useEffect(
    function scrollToTargetAfterImportRecalcSettles() {
      if (!isActiveRef.current || !landingRecalcPendingRef.current) return undefined;
      if (!hydrationApplied || isHardLoading) return undefined;

      const justFinishedCalc = wasCalculatingRef.current && !isCalculating;
      wasCalculatingRef.current = isCalculating;
      if (!justFinishedCalc) return undefined;

      landingRecalcPendingRef.current = false;
      const shouldLandOnChart = scrollToChartAfterRecalcRef.current && !showTransitionModal;
      scrollToChartAfterRecalcRef.current = false;

      const frame = requestAnimationFrame(function applyPostRecalcScroll() {
        if (shouldLandOnChart) scrollToChart("smooth");
        // While the modal is open the anchor effect owns positioning, so don't snap the user here.
        else if (!showTransitionModal) scrollToBuilder({ behavior: "instant" });
      });
      return function cancelPostRecalcScroll() {
        cancelAnimationFrame(frame);
      };
    },
    [
      hydrationApplied,
      isHardLoading,
      isCalculating,
      showTransitionModal,
      scrollToBuilder,
      scrollToChart,
      isActiveRef,
      scrollToChartAfterRecalcRef,
      landingRecalcPendingRef,
    ]
  );
};
